
import { isSaturday, isSunday, isBefore, addDays, differenceInMinutes } from 'date-fns';

// Rates definition
const RATE_BASE = 20; // Base hour for everyone
const RATE_NIGHT_PREMIUM = 6; // Additional for night/weekend/holiday (Total 26)
const RATE_BILINGUAL_TARGET = 35; // Target total for bilingual hours

const DAY_START_HOUR = 6;
const DAY_END_HOUR = 20; // 20:00 starts night shift

// Helper for parsing YYYY-MM-DD
const parseDateString = (dateStr: string): Date => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Helper for parsing HH:mm
const parseTimeString = (timeStr: string, baseDate: Date): Date => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const d = new Date(baseDate);
  d.setHours(hours, minutes, 0, 0);
  return d;
};

// Helper to get minutes from midnight for math calculations
const getMinutesFromMidnight = (timeStr: string): number => {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
};

/**
 * Calculates just the hours breakdown (Day vs Night) for visualization.
 * Optimized to use mathematical intervals instead of loops for O(1) performance.
 */
export const calculateHoursBreakdown = (
  dateStr: string,
  startTimeStr: string,
  endTimeStr: string
) => {
  const startMinutes = getMinutesFromMidnight(startTimeStr);
  let endMinutes = getMinutesFromMidnight(endTimeStr);

  // Handle overnight shifts (e.g., 18:00 to 02:00)
  // If end is smaller than start, it means it crossed midnight
  if (endMinutes < startMinutes) {
    endMinutes += 1440; // Add 24h (1440 mins)
  }

  const totalMinutes = endMinutes - startMinutes;

  // Define Day Windows (06:00 - 20:00) in minutes
  // Window 1: Current Day 06:00 (360) to 20:00 (1200)
  const dayStart1 = DAY_START_HOUR * 60; 
  const dayEnd1 = DAY_END_HOUR * 60;     

  // Window 2: Next Day 06:00 (1800) to 20:00 (2640)
  // Required for shifts that start in the evening and go past 06:00 the next day
  const dayStart2 = dayStart1 + 1440;    
  const dayEnd2 = dayEnd1 + 1440;        

  // Function to calculate overlap between two intervals [s, e] and [wS, wE]
  const getOverlap = (s: number, e: number, wS: number, wE: number) => {
    const start = Math.max(s, wS);
    const end = Math.min(e, wE);
    return Math.max(0, end - start);
  };

  // Calculate day minutes by checking overlap with today's day window and tomorrow's day window
  const dayMinutes = getOverlap(startMinutes, endMinutes, dayStart1, dayEnd1) +
                     getOverlap(startMinutes, endMinutes, dayStart2, dayEnd2);

  const nightMinutes = totalMinutes - dayMinutes;

  return {
    dayHours: parseFloat((dayMinutes / 60).toFixed(2)),
    nightHours: parseFloat((nightMinutes / 60).toFixed(2))
  };
};

/**
 * Calculates earnings for a given shift with detailed breakdown.
 * Uses strict iteration logic to ensure consistency with complex rules (Weekend/Holiday/Bilingual).
 */
export const calculateEarnings = (
  dateStr: string,
  startTimeStr: string,
  endTimeStr: string,
  isHoliday: boolean,
  bilingualStartStr?: string,
  bilingualEndStr?: string,
  generation: 'OLD' | 'NEW' = 'OLD'
) => {
  // Base date
  const baseDate = parseDateString(dateStr);
  
  // Construct full Date objects
  let start = parseTimeString(startTimeStr, baseDate);
  let end = parseTimeString(endTimeStr, baseDate);
  
  // Handle overnight shifts (e.g., 22:00 to 05:00)
  if (isBefore(end, start)) {
    end = addDays(end, 1);
  }

  // Bilingual setup
  let biStart: Date | null = null;
  let biEnd: Date | null = null;

  if (bilingualStartStr && bilingualEndStr) {
    biStart = parseTimeString(bilingualStartStr, baseDate);
    biEnd = parseTimeString(bilingualEndStr, baseDate);
    
    // Adjust dates if bilingual part crosses midnight or matches shift dates logic
    // We normalize bilingual window relative to the shift start
    if (isBefore(biStart, start) && differenceInMinutes(start, biStart) > 720) {
       // Likely next day overlap
       biStart = addDays(biStart, 1);
       biEnd = addDays(biEnd, 1);
    } else if (isBefore(biStart, start) && isBefore(end, start)) {
       // Shift handles overnight, bilingual follows
       biStart = addDays(biStart, 1);
       biEnd = addDays(biEnd, 1);
    } else if (isBefore(biEnd, biStart)) {
       biEnd = addDays(biEnd, 1);
    }
  }

  // Accumulators
  let totalBasePay = 0;
  let totalNightBonus = 0;
  let totalBilingualBonus = 0;
  
  // Calculate total duration in minutes
  const totalMinutes = Math.round((end.getTime() - start.getTime()) / 60000);
  let current = new Date(start);

  for (let i = 0; i < totalMinutes; i++) {
    // 1. Calculate Base Pay (Always 20/h) per minute
    const minuteBaseVal = RATE_BASE / 60;
    totalBasePay += minuteBaseVal;

    // 2. Determine Logic State (Night/Weekend/Holiday)
    let isPremiumTime = false;
    
    // Check Weekend
    const isWknd = isSaturday(current) || isSunday(current);
    
    // Check Night Time (Mon-Fri)
    const hour = current.getHours();
    const isNightHour = hour < DAY_START_HOUR || hour >= DAY_END_HOUR;

    if (isHoliday || isWknd || isNightHour) {
        if (generation === 'OLD') {
            isPremiumTime = true;
        }
    }

    // 3. Apply Night Bonus if applicable
    let currentTotalRate = RATE_BASE;
    if (isPremiumTime) {
        const minuteNightVal = RATE_NIGHT_PREMIUM / 60;
        totalNightBonus += minuteNightVal;
        currentTotalRate += RATE_NIGHT_PREMIUM; // Becomes 26
    }

    // 4. Apply Bilingual Top-Up if applicable
    const currentTime = current.getTime();
    const isBilingualActive = biStart && biEnd && 
                        (currentTime >= biStart.getTime() && currentTime < biEnd.getTime());

    if (isBilingualActive) {
        // We need to reach 35. 
        // Gap = 35 - CurrentRate (20 or 26)
        const gap = RATE_BILINGUAL_TARGET - currentTotalRate;
        if (gap > 0) {
            totalBilingualBonus += gap / 60;
        }
    }

    // Advance 1 minute
    current.setTime(current.getTime() + 60000);
  }

  const totalEarnings = totalBasePay + totalNightBonus + totalBilingualBonus;

  return {
    totalEarnings: parseFloat(totalEarnings.toFixed(2)),
    totalHours: parseFloat((totalMinutes / 60).toFixed(2)),
    breakdown: {
        basePay: parseFloat(totalBasePay.toFixed(2)),
        nightPay: parseFloat(totalNightBonus.toFixed(2)),
        bilingualPay: parseFloat(totalBilingualBonus.toFixed(2))
    }
  };
};
